import {Box, Paper, Typography} from "@mui/material";
import {useMemo, useState} from "react";
import {feature} from "topojson-client";
import {geoNaturalEarth1, geoPath} from "d3-geo";
import countries from "i18n-iso-countries";
import frLocale from "i18n-iso-countries/langs/fr.json";
import worldTopology from "world-atlas/countries-110m.json";
import {sequentialColorForRatio} from "./chartColors";

countries.registerLocale(frLocale);

const WIDTH = 960;
const HEIGHT = 500;
const NEUTRAL_FILL = '#3a3a38';
const TOOLTIP_OFFSET = 16;

// Précalculé une seule fois au chargement du module (ne dépend pas des props).
const FEATURES = (() => {
    const geojson = feature(worldTopology, worldTopology.objects.countries);
    const projection = geoNaturalEarth1().fitSize([WIDTH, HEIGHT], geojson);
    const pathGenerator = geoPath(projection);

    return geojson.features.map((f) => ({
        numericId: f.id,
        name: f.properties.name,
        path: pathGenerator(f),
    }));
})();

/**
 * Carte du monde interactive : chaque pays est coloré selon son nombre de scans
 * (rampe séquentielle bleue). Survol -> tooltip (nom, scans du jour, total période).
 * Clic sur un pays -> onCountryClick(alpha2, name).
 */
function WorldMap({data, selectedCountry, onCountryClick}) {
    const [hoveredId, setHoveredId] = useState(null);
    const [mousePos, setMousePos] = useState({x: 0, y: 0});

    const {countByNumericId, todayCountByNumericId, nameByNumericId, alpha2ByNumericId, maxCount} = useMemo(() => {
        const byId = new Map();
        const todayById = new Map();
        const names = new Map();
        const alpha2s = new Map();
        let max = 0;

        data.forEach(({label, count, todayCount}) => {
            const numericId = countries.alpha2ToNumeric(label);
            if (!numericId) return; // labels type "Inconnu" (pas de géoloc)
            byId.set(numericId, count);
            todayById.set(numericId, todayCount || 0);
            alpha2s.set(numericId, label);
            names.set(numericId, countries.getName(label, 'fr') || label);
            if (count > max) max = count;
        });

        return {countByNumericId: byId, todayCountByNumericId: todayById, nameByNumericId: names, alpha2ByNumericId: alpha2s, maxCount: max};
    }, [data]);

    const selectedNumericId = selectedCountry ? countries.alpha2ToNumeric(selectedCountry) : null;

    const hovered = hoveredId ? {
        name: nameByNumericId.get(hoveredId) || FEATURES.find((f) => f.numericId === hoveredId)?.name,
        count: countByNumericId.get(hoveredId) || 0,
        todayCount: todayCountByNumericId.get(hoveredId) || 0,
    } : null;

    return (
        <Box
            sx={{width: '100%', overflowX: 'auto', position: 'relative'}}
            onMouseMove={(e) => setMousePos({x: e.clientX, y: e.clientY})}
        >
            <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} style={{width: '100%', height: 'auto', display: 'block'}}>
                {FEATURES.map((f) => {
                    const count = countByNumericId.get(f.numericId) || 0;
                    const alpha2 = alpha2ByNumericId.get(f.numericId);
                    const isSelected = f.numericId === selectedNumericId;
                    const fill = count > 0 ? sequentialColorForRatio(count / maxCount) : NEUTRAL_FILL;

                    return (
                        <path
                            key={f.numericId}
                            d={f.path}
                            fill={fill}
                            stroke={isSelected ? '#FF7233' : 'rgba(0,0,0,0.25)'}
                            strokeWidth={isSelected ? 2 : 0.5}
                            style={{cursor: alpha2 ? 'pointer' : 'default'}}
                            onMouseEnter={() => setHoveredId(f.numericId)}
                            onMouseLeave={() => setHoveredId((current) => (current === f.numericId ? null : current))}
                            onClick={() => alpha2 && onCountryClick(alpha2, nameByNumericId.get(f.numericId) || f.name)}
                        />
                    );
                })}
            </svg>

            {hovered && (
                <Paper
                    elevation={3}
                    sx={{
                        position: 'fixed',
                        left: mousePos.x + TOOLTIP_OFFSET,
                        top: mousePos.y + TOOLTIP_OFFSET,
                        px: 1.5, py: 1,
                        pointerEvents: 'none',
                        zIndex: 1300,
                    }}
                >
                    <Typography variant="subtitle2">{hovered.name}</Typography>
                    <Typography variant="body2">
                        Aujourd'hui : <strong>{hovered.todayCount}</strong>
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        Total période : {hovered.count}
                    </Typography>
                </Paper>
            )}

            {maxCount === 0 && (
                <Typography variant="body2" color="text.secondary" textAlign="center">
                    Pas encore de données de géolocalisation
                </Typography>
            )}
        </Box>
    );
}

export default WorldMap;
